import React, { useState, useEffect, useMemo } from 'react';
import { Icon } from './Icon.js';
import './ProtocolScheduler.css';

// Unit options (for "every X days/weeks/months")
const UNIT_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' }
];

// Generate interval number options (1-30)
const INTERVAL_NUMBERS = Array.from({ length: 30 }, (_, i) => i + 1);

// Format time from 24h to 12h with AM/PM
const formatTimeDisplay = (timeValue) => {
  if (!timeValue) return '';
  const [hour = '', minute = ''] = timeValue.split(':');
  if (hour.length === 0 || minute.length === 0) return timeValue;
  const hourNum = parseInt(hour, 10);
  if (Number.isNaN(hourNum)) return timeValue;
  const suffix = hourNum >= 12 ? 'PM' : 'AM';
  const normalizedHour = ((hourNum + 11) % 12) + 1;
  return `${normalizedHour}:${minute} ${suffix}`;
};

const ProtocolScheduler = ({
  selectedHacks,
  onScheduleChange,
  onClose
}) => {
  const [expandedBiohacks, setExpandedBiohacks] = useState({});
  const [scheduleData, setScheduleData] = useState({});
  const [scheduleOption, setScheduleOption] = useState(null); // 'normal' or 'none'
  const [bulkTime, setBulkTime] = useState('');
  const [bulkInterval, setBulkInterval] = useState('');
  const [bulkIntervalUnit, setBulkIntervalUnit] = useState('');
  const [confirmedSchedules, setConfirmedSchedules] = useState(new Set()); // Track which biohacks have confirmed schedules

  // Initialize schedule data for all biohacks
  useEffect(() => {
    const initialData = {};
    selectedHacks.forEach(hack => {
      initialData[hack.id] = {
        times: [],
        interval: null,
        intervalUnit: null
      };
    });
    setScheduleData(initialData);
    // Reset confirmed schedules when biohacks change
    setConfirmedSchedules(new Set());
  }, [selectedHacks]);

  // Notify parent when schedule option or confirmed schedules change
  useEffect(() => {
    if (onScheduleChange) {
      onScheduleChange(scheduleOption, scheduleData, confirmedSchedules);
    }
  }, [scheduleOption, scheduleData, confirmedSchedules, onScheduleChange]);

  // Toggle biohack expansion
  const toggleBiohackExpansion = (biohackId) => {
    setExpandedBiohacks(prev => ({
      ...prev,
      [biohackId]: !prev[biohackId]
    }));
  };

  // Handle "Done" button - collapse the row and mark schedule as confirmed
  const handleRowDone = (biohackId) => {
    // Check if schedule is complete before confirming
    const schedule = scheduleData[biohackId];
    const hasInterval = schedule?.interval !== null && schedule?.interval !== undefined;
    const hasUnit = schedule?.intervalUnit !== null && schedule?.intervalUnit !== undefined;
    const hasTime = schedule?.times && schedule.times.length > 0 && schedule.times[0];
    
    if (hasInterval && hasUnit && hasTime) {
      // Mark this biohack's schedule as confirmed
      setConfirmedSchedules(prev => new Set([...prev, biohackId]));
    }
    
    setExpandedBiohacks(prev => ({
      ...prev,
      [biohackId]: false
    }));
  };

  // Update biohack schedule
  const updateBiohackSchedule = (biohackId, field, value) => {
    // If this biohack had a confirmed schedule and we're editing it, unconfirm it
    if (confirmedSchedules.has(biohackId)) {
      setConfirmedSchedules(prev => {
        const newSet = new Set(prev);
        newSet.delete(biohackId);
        return newSet;
      });
    }
    
    setScheduleData(prev => ({
      ...prev,
      [biohackId]: {
        ...prev[biohackId] || { times: [], interval: null, intervalUnit: null },
        [field]: value
      }
    }));
  };

  // Apply bulk settings to all biohacks that don't have schedules yet
  const applyBulkSettings = () => {
    if (!bulkInterval || !bulkIntervalUnit || !bulkTime) return;

    const updatedData = { ...scheduleData };
    const newlyConfirmed = [];
    
    Object.keys(updatedData).forEach(biohackId => {
      const currentSchedule = updatedData[biohackId];
      // Only apply to biohacks without schedules or with empty schedules
      if (!currentSchedule.interval || !currentSchedule.intervalUnit) {
        if (bulkInterval && bulkIntervalUnit) {
          updatedData[biohackId].interval = parseInt(bulkInterval, 10);
          updatedData[biohackId].intervalUnit = bulkIntervalUnit;
        }
        if (bulkTime) {
          updatedData[biohackId].times = [bulkTime];
        }
        // Mark as confirmed since we're applying a complete schedule
        newlyConfirmed.push(biohackId);
      }
    });
    
    setScheduleData(updatedData);
    
    // Mark all newly scheduled biohacks as confirmed
    if (newlyConfirmed.length > 0) {
      setConfirmedSchedules(prev => new Set([...prev, ...newlyConfirmed]));
    }
    
    // Clear bulk inputs after applying
    setBulkTime('');
    setBulkInterval('');
    setBulkIntervalUnit('');
  };

  const handleScheduleOptionChange = (option) => {
    setScheduleOption(option);
    if (option === 'none') {
      // Clear all schedule data and confirmed schedules when "no schedule" is selected
      const clearedData = {};
      selectedHacks.forEach(hack => {
        clearedData[hack.id] = {
          times: [],
          interval: null,
          intervalUnit: null
        };
      });
      setScheduleData(clearedData);
      setConfirmedSchedules(new Set());
    }
  };

  return (
    <div className="protocol-scheduler">
      {/* Header */}
      <div className="protocol-scheduler-header">
        <div className="protocol-scheduler-header-content">
          <div className="protocol-scheduler-title-section">
            <h2 className="protocol-scheduler-header-title">
              <span>Logging Schedule</span>
            </h2>
          </div>
          <button
            className="protocol-scheduler-close-btn"
            onClick={onClose}
            aria-label="Close schedule"
          >
            <Icon name="X" />
          </button>
        </div>
      </div>

      {/* Schedule Option Selection */}
      <div className="protocol-scheduler-options">
        <button
          className={`protocol-scheduler-option ${scheduleOption === 'normal' ? 'active' : ''}`}
          onClick={() => handleScheduleOptionChange('normal')}
        >
          <Icon name="Calendar" className="icon--sm" />
          <span>Set Schedule</span>
        </button>
        <button
          className={`protocol-scheduler-option ${scheduleOption === 'none' ? 'active' : ''}`}
          onClick={() => handleScheduleOptionChange('none')}
        >
          <Icon name="XCircle" className="icon--sm" />
          <span>No Schedule Now</span>
        </button>
      </div>

      {/* Body - Only show if "Set Schedule" is selected */}
      {scheduleOption === 'normal' && (
        <div className="protocol-scheduler-body">
          <div className="protocol-scheduler-content">
            {/* Biohack Rows */}
            {selectedHacks.length === 0 ? (
              <div className="protocol-scheduler-no-biohacks">
                <p>No biohacks in this protocol</p>
              </div>
            ) : (
              selectedHacks.map(biohack => (
                <BiohackRow
                  key={biohack.id}
                  biohack={biohack}
                  isExpanded={expandedBiohacks[biohack.id] || false}
                  scheduleData={scheduleData[biohack.id] || { times: [], interval: null, intervalUnit: null }}
                  onToggleExpansion={() => toggleBiohackExpansion(biohack.id)}
                  onUpdateSchedule={(field, value) => updateBiohackSchedule(biohack.id, field, value)}
                  onDone={() => handleRowDone(biohack.id)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Show muted text when "No Schedule Now" is selected */}
      {scheduleOption === 'none' && (
        <div className="protocol-scheduler-body">
          <div className="protocol-scheduler-content">
            <div className="protocol-scheduler-info-text">
              <p>You can log a protocol at any time - no schedule required! Just go to the Logging tab.</p>
              <p>To set or update logging schedules for active protocols, open the Protocols tab on the user page, click the protocol card FAB menu, and select Schedule.</p>
            </div>
          </div>
        </div>
      )}

      {/* Footer - Advanced Scheduling Panel - Only show if "Set Schedule" is selected */}
      {scheduleOption === 'normal' && (
        <div className="protocol-scheduler-footer">
          <AdvancedSchedulingPanel
            bulkTime={bulkTime}
            bulkInterval={bulkInterval}
            bulkIntervalUnit={bulkIntervalUnit}
            onBulkTimeChange={setBulkTime}
            onBulkIntervalChange={setBulkInterval}
            onBulkIntervalUnitChange={setBulkIntervalUnit}
            onApplyBulk={applyBulkSettings}
          />
        </div>
      )}
    </div>
  );
};

// Biohack Row Component (collapsed and expanded)
const BiohackRow = ({
  biohack,
  isExpanded,
  scheduleData,
  onToggleExpansion,
  onUpdateSchedule,
  onDone
}) => {
  const [validationErrors, setValidationErrors] = useState({
    interval: false,
    intervalUnit: false,
    time: false
  });

  const displayInterval = scheduleData.interval !== null && scheduleData.interval !== undefined 
    ? scheduleData.interval 
    : null;
  const displayIntervalUnit = scheduleData.intervalUnit || null;

  // Determine if schedule is set - requires all three: number, unit, and time
  const hasNumber = scheduleData.interval !== null && scheduleData.interval !== undefined;
  const hasUnit = scheduleData.intervalUnit !== null && scheduleData.intervalUnit !== undefined;
  const hasTime = scheduleData.times && scheduleData.times.length > 0 && scheduleData.times[0];
  const hasSchedule = hasNumber && hasUnit && hasTime;
  
  let scheduleSummary = 'Not Selected';
  if (hasSchedule) {
    const unitLabel = UNIT_OPTIONS.find(opt => opt.value === scheduleData.intervalUnit)?.label || scheduleData.intervalUnit;
    scheduleSummary = `Every ${scheduleData.interval} ${unitLabel.toLowerCase()} @ ${formatTimeDisplay(scheduleData.times[0])}`;
  }

  const handleDone = (e) => {
    e.stopPropagation();
    
    // Validate all three fields are filled
    const errors = {
      interval: !hasNumber,
      intervalUnit: !hasUnit,
      time: !hasTime
    };

    // Check if there are any errors
    if (errors.interval || errors.intervalUnit || errors.time) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous errors
    setValidationErrors({ interval: false, intervalUnit: false, time: false });
    onDone();
  };

  const handleIntervalChange = (field, value) => {
    const hasValue = value !== null && value !== undefined && value !== '';
    
    // Clear error when user makes a selection
    if (validationErrors[field] || validationErrors.intervalUnit) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: false,
        ...(field === 'interval' ? { intervalUnit: false } : {})
      }));
    }
    
    if (field === 'interval') {
      const intervalValue = hasValue ? parseInt(value, 10) : null;
      onUpdateSchedule('interval', intervalValue);
      // If clearing interval, also clear unit
      if (!intervalValue) {
        onUpdateSchedule('intervalUnit', null);
      }
    } else if (field === 'intervalUnit') {
      onUpdateSchedule('intervalUnit', hasValue ? value : null);
      // If clearing unit, also clear interval
      if (!hasValue) {
        onUpdateSchedule('interval', null);
      }
    }
  };

  const handleTimeChange = (timeValue) => {
    // Clear error when user makes a selection
    if (validationErrors.time) {
      setValidationErrors(prev => ({ ...prev, time: false }));
    }
    
    if (timeValue) {
      onUpdateSchedule('times', [timeValue]);
    } else {
      onUpdateSchedule('times', []);
    }
  };

  return (
    <div className="biohack-row">
      <div className="biohack-row-collapsed">
        <div className="biohack-row-header">
          <div className="biohack-row-info">
            <img
              src={biohack.image_url || '/default-biohack.jpg'}
              alt={biohack.name}
              className="biohack-row-image"
              onError={(e) => {
                e.target.src = '/default-biohack.jpg';
              }}
            />
            <span className="biohack-row-name">{biohack.name}</span>
          </div>
          <div className="biohack-row-schedule-section">
            <div className={`biohack-row-summary ${!hasSchedule ? 'biohack-row-summary--empty' : ''}`}>
              {scheduleSummary}
            </div>
          </div>
        </div>
        <div className="biohack-row-expand-control" onClick={onToggleExpansion}>
          <Icon
            name="ChevronDown"
            className={`biohack-row-chevron ${isExpanded ? 'rotated' : ''}`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="biohack-row-expanded">
          <div className="biohack-row-controls biohack-row-controls--advanced">
            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Number</label>
              <div className="biohack-row-select-wrapper">
                <select
                  value={displayInterval || ''}
                  onChange={(e) => handleIntervalChange('interval', e.target.value)}
                  className={`biohack-row-select ${!displayInterval ? 'biohack-row-select--placeholder' : ''} ${validationErrors.interval ? 'biohack-row-select--error' : ''}`}
                  data-placeholder="1"
                >
                  <option value=""></option>
                  {INTERVAL_NUMBERS.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                {!displayInterval && (
                  <span className="biohack-row-select-placeholder">1</span>
                )}
              </div>
            </div>

            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Unit</label>
              <div className="biohack-row-select-wrapper">
                <select
                  value={displayIntervalUnit || ''}
                  onChange={(e) => handleIntervalChange('intervalUnit', e.target.value)}
                  className={`biohack-row-select ${!displayIntervalUnit ? 'biohack-row-select--placeholder' : ''} ${validationErrors.intervalUnit ? 'biohack-row-select--error' : ''}`}
                  data-placeholder="Days"
                >
                  <option value=""></option>
                  {UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!displayIntervalUnit && (
                  <span className="biohack-row-select-placeholder">Days</span>
                )}
              </div>
            </div>

            <div className="biohack-row-control-group">
              <label className="biohack-row-label">Time</label>
              <input
                type="time"
                value={scheduleData.times && scheduleData.times.length > 0 ? scheduleData.times[0] : ''}
                onChange={(e) => handleTimeChange(e.target.value)}
                className={`biohack-row-time-input ${validationErrors.time ? 'biohack-row-time-input--error' : ''}`}
              />
            </div>

            <button 
              className="biohack-row-save-btn"
              onClick={handleDone}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Advanced Scheduling Panel Component
const AdvancedSchedulingPanel = ({
  bulkTime,
  bulkInterval,
  bulkIntervalUnit,
  onBulkTimeChange,
  onBulkIntervalChange,
  onBulkIntervalUnitChange,
  onApplyBulk
}) => {
  const [isExpanded, setIsExpanded] = useState(window.innerWidth > 500);
  const isDisabled = !bulkInterval || !bulkIntervalUnit || !bulkTime;

  // Handle resize to ensure it stays expanded on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 500) {
        setIsExpanded(true);
      } else {
        // Optional: collapse when resizing down to mobile? 
        // User asked for "collapsed by default" on mobile.
        // If we resize from desktop to mobile, keeping it open is probably fine/better UX,
        // but if we want strict adherence to "default collapsed", we might not force it.
        // I will leave it as is (user controls it on mobile once set).
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleExpand = () => {
    if (window.innerWidth <= 500) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={`advanced-scheduling-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div 
        className="advanced-scheduling-header" 
        onClick={toggleExpand}
        role={window.innerWidth <= 500 ? "button" : undefined}
      >
        <h3 className="advanced-scheduling-title">
          <Icon name="Calendar" className="icon--sm" />
          Apply Schedule to All Biohacks
        </h3>
        <div className="advanced-scheduling-toggle-icon">
          <Icon name="ChevronDown" className="icon--sm" />
        </div>
      </div>
      
      <div className="advanced-scheduling-wrapper">
        <div className="advanced-scheduling-content">
          <h4 className="advanced-scheduling-drawer-title">
            Apply Schedule to All Biohacks
          </h4>
          <div className="advanced-scheduling-controls advanced-scheduling-controls--advanced">
            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Number</label>
              <div className="advanced-scheduling-select-wrapper">
                <select
                  value={bulkInterval}
                  onChange={(e) => onBulkIntervalChange(e.target.value)}
                  className={`advanced-scheduling-select ${!bulkInterval ? 'advanced-scheduling-select--placeholder' : ''}`}
                  data-placeholder="1"
                >
                  <option value=""></option>
                  {INTERVAL_NUMBERS.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                {!bulkInterval && (
                  <span className="advanced-scheduling-select-placeholder">1</span>
                )}
              </div>
            </div>

            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Unit</label>
              <div className="advanced-scheduling-select-wrapper">
                <select
                  value={bulkIntervalUnit}
                  onChange={(e) => onBulkIntervalUnitChange(e.target.value)}
                  className={`advanced-scheduling-select ${!bulkIntervalUnit ? 'advanced-scheduling-select--placeholder' : ''}`}
                  data-placeholder="Days"
                >
                  <option value=""></option>
                  {UNIT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {!bulkIntervalUnit && (
                  <span className="advanced-scheduling-select-placeholder">Days</span>
                )}
              </div>
            </div>

            <div className="advanced-scheduling-group">
              <label className="advanced-scheduling-label">Time</label>
              <input
                type="time"
                value={bulkTime}
                onChange={(e) => onBulkTimeChange(e.target.value)}
                className="advanced-scheduling-time-input"
              />
            </div>

            <button
              className="advanced-scheduling-apply-btn"
              onClick={onApplyBulk}
              disabled={isDisabled}
            >
              Apply to all unscheduled biohacks
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtocolScheduler;

